import React from 'react';
import { Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';

// ── Category icon palette for CIP harvest records ────────────────────────────
const CATEGORY_ICONS = {
  plants_medicine: '🌿',
  hunting:         '🦌',
  birds:           '🦅',
  fishing:         '🐟',
  environmental:   '🌍',
  other:           '📍',
};

const createCustomIcon = (record, dataTypeConfig) => {
  const icon =
    (dataTypeConfig?.iconPalette && dataTypeConfig.iconPalette[record.category]) ||
    CATEGORY_ICONS[record.category] ||
    dataTypeConfig?.icon ||
    '📍';

  const html = renderToStaticMarkup(
    <div style={{
      fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '100%', height: '100%', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.3))',
    }}>
      {icon}
    </div>
  );

  return L.divIcon({
    html,
    className: 'cip-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const parseLocation = (record) => {
  try {
    if (record.location?.coordinates) {
      // GeoJSON Point [lng, lat] → Leaflet [lat, lng]
      const [lng, lat] = record.location.coordinates;
      return [lat, lng];
    }
    if (Array.isArray(record.location)) return record.location;
  } catch (_) {}
  return null;
};

const parseGeoJson = (record) => {
  try {
    if (!record.mapData) return null;
    const geo = typeof record.mapData === 'string' ? JSON.parse(record.mapData) : record.mapData;
    if (geo?.type) return geo;
  } catch (_) {}
  return null;
};

const geojsonStyle = (config) => ({
  color: config?.color || '#4A90E2',
  weight: 2,
  opacity: 0.8,
  fillOpacity: 0.2,
});

/**
 * LayerRenderer — renders CIP HarvestRecord (or any compatible) data onto a react-leaflet map.
 *
 * Props:
 *   dataTypeConfig  — { icon, color, name, iconPalette?, mapConfig?.popupTemplate? }
 *   data            — array of records to render
 *   onFeatureClick  — optional callback(record)
 */
const LayerRenderer = ({ dataTypeConfig, data, onFeatureClick }) => {
  if (!data?.length) return null;

  const makePopup = (record) => {
    if (dataTypeConfig?.mapConfig?.popupTemplate) {
      return dataTypeConfig.mapConfig.popupTemplate(record);
    }
    const label = record.species || record.subcategory || record.category || 'Record';
    const date  = record.harvestDate
      ? new Date(record.harvestDate).toLocaleDateString()
      : record.createdAt
        ? new Date(record.createdAt).toLocaleDateString()
        : '';
    return `<div style="min-width:160px"><strong>${label}</strong>${date ? `<br/><small>${date}</small>` : ''}</div>`;
  };

  const onEachFeature = (feature, layer) => {
    const rec = feature.properties?.originalData;
    if (rec) layer.bindPopup(makePopup(rec));
  };

  return (
    <>
      {data.filter(r => parseLocation(r)).map((r, i) => {
        const pos = parseLocation(r);
        return (
          <Marker
            key={r._id || i}
            position={pos}
            icon={createCustomIcon(r, dataTypeConfig)}
            eventHandlers={{ click: () => onFeatureClick && onFeatureClick(r) }}
          >
            <Popup><div dangerouslySetInnerHTML={{ __html: makePopup(r) }} /></Popup>
          </Marker>
        );
      })}

      {data.filter(r => parseGeoJson(r)).map((r, i) => {
        const geo = parseGeoJson(r);
        const enhanced = {
          ...geo,
          properties: { ...geo.properties, originalData: r },
        };
        if (enhanced.type === 'FeatureCollection') {
          enhanced.features = enhanced.features.map(f => ({
            ...f,
            properties: { ...f.properties, originalData: r },
          }));
        }
        return (
          <GeoJSON
            key={`geo-${r._id || i}`}
            data={enhanced}
            style={() => geojsonStyle(dataTypeConfig)}
            onEachFeature={onEachFeature}
          />
        );
      })}
    </>
  );
};

export default LayerRenderer;
